import { createElement } from 'react';
import * as Yup from 'yup';

import { isInvoiceBillingType } from '~constants/maps';

import { formatDateString, getDaysMultiSelectOptions } from '~libs/dayjs';

import { TooltipIcon } from '~components';
import Icon from '~components/icon';
import {
  faArrowRightArrowLeft,
  faCircleExclamation,
  faCircleXmark,
  faInfoCircle,
  faSolidCircleCheck,
  faSpinner,
  faTriangleExclamation,
} from '~components/icon/iconLibrary';
import { Column, EditorContext, Row } from '~components/table-view';
import { dateEditor, multiSelectEditor, selectLazyEditor } from '~components/table-view/columnEditors';
import { currencyCellRender, genericCellRender } from '~components/table-view/columnRenderers';

import { Lookup, SahCategory, Shift } from '~lookups/models';
import { COPY_STATUS, CopyStatus, REVIEW_STATE, ReviewState, Section, SectionItem } from '~support-at-home/models';

import ExternalProvider from './ExternalProvider';
import {
  DateRange,
  deriveItemDates,
  findServiceDetail,
  formatRange,
  hasItemDateChanged,
  isWithinPeriod,
} from './servicePeriods';

interface ServiceType {
  id: number;
  name: string;
  sah_service: { category: SahCategory };
}

const isInvoiceType = (row: Row) => {
  if (!row?.billing_type) return false;
  return isInvoiceBillingType(row?.billing_type);
};

/**
 * The offered service behind a row.
 *
 * Matched on the service type AND the section's category together. The same local service type can
 * now be offered under two categories at once - one per period of the Support at Home service it
 * resolves to - so the service type alone no longer identifies a single entry. Finding by id and
 * then testing the category, as this did, returns whichever period sorts first and declares the
 * row unmatched for the other: the Clinical Support line of a split service rendered as "no longer
 * valid or has been filtered", uneditable, with its price blanked.
 */
const findMatchingService = (row: Row, context?: EditorContext, filteredServices?: any) => {
  const serviceTypeId = row?.service_type_id;
  const categoryId = (row?.category_id ?? context?.category_id) as string | undefined;

  if (!serviceTypeId || !categoryId || !filteredServices) return null;

  return (
    filteredServices.find(
      (service: any) => service.id === serviceTypeId && service.sah_service?.category?.id === categoryId,
    ) ?? null
  );
};

const isMatchedService = (row: Row, context?: EditorContext, filteredServices?: any) => {
  const categoryId = row?.category_id ?? context?.category_id;
  if (!row?.service_type_id || !categoryId || !filteredServices) return true;
  return findMatchingService(row, context, filteredServices) !== null;
};

const isIgnoredService = (row: Row, context?: EditorContext, filteredServices?: any) => {
  const service = findMatchingService(row, context, filteredServices);
  return service?.sah_ignored === true;
};

/**
 * The Support at Home service period behind a row, and its name.
 *
 * The section's category is part of the lookup: the same local service type can now sit on more
 * than one Support at Home service, one per period, and the two are told apart by the contribution
 * category they carry. See findServicePeriod for why matching on the service type alone is not
 * enough.
 */
const rowServiceDetail = (row: Row, context?: EditorContext, filteredServices?: any) =>
  findServiceDetail(row?.service_type_id, (row?.category_id ?? context?.category_id) as string, filteredServices ?? []);

const rowDateRange = (row: Row): DateRange => ({
  start_date: row?.start_date ?? null,
  end_date: row?.end_date ?? null,
});

/** Is a row dated outside the period its service is available for? */
const isOutsideServicePeriod = (row: Row, context?: EditorContext, filteredServices?: any): boolean => {
  if (!row?.start_date || !row?.end_date) return false;

  const detail = rowServiceDetail(row, context, filteredServices);
  if (!detail) return false;

  return !isWithinPeriod(rowDateRange(row), detail.period);
};

/** Is a row dated outside the budget it belongs to? */
const isOutsideBudget = (row: Row, context?: EditorContext): boolean => {
  const budgetStart = context?.start_date as string | undefined;
  const budgetEnd = context?.end_date as string | undefined;
  if (!row?.start_date || !row?.end_date || !budgetStart || !budgetEnd) return false;

  return !isWithinPeriod(rowDateRange(row), { start_date: budgetStart, end_date: budgetEnd });
};

/**
 * A row's date changed by the refresh control, against what was loaded.
 *
 * Mirrors how a refreshed co-contribution is shown on the section header: the value that moved is
 * highlighted so the provider can see what the refresh did rather than having to compare by eye.
 */
const isDateRefreshed = (row: Row, context?: EditorContext): boolean =>
  hasItemDateChanged(row, (context?.initialRows ?? []) as SectionItem[]);

/**
 * The one removal reason the server distinguishes: the service is on the target price plan, but not
 * available under this contribution category for the dates being copied into.
 */
const REMOVED_REASON_UNAVAILABLE_FOR_DATES = 'unavailable_for_dates';

const isRemovedCopyItem = (row: Row) => row?.copy_status === COPY_STATUS.REMOVED;


const isRemovedReviewState = (reviewState?: ReviewState) => reviewState === REVIEW_STATE.NOT_ON_PLAN;

const isFlaggedReviewState = (reviewState?: ReviewState) =>
  reviewState === REVIEW_STATE.NOT_ON_PLAN ||
  reviewState === REVIEW_STATE.FILTERED ||
  reviewState === REVIEW_STATE.PRICE_MISSING;

// `Row` is the table-view's loosely-typed view model, so the model-typed `review_state`
// (ReviewState | undefined on SectionItem) needs a cast to read it here. Keep that cast in one
// place so the render and deriveValue paths can never drift apart.
const getReviewState = (row: Row): ReviewState | undefined => row?.review_state as ReviewState | undefined;

/**
 * A row the provider cannot change, because nothing they could change would make it save.
 *
 * A line carried into a different contribution category is deliberately NOT one of these. It is
 * saved, unlike a removed line, so locking it would leave a provider looking at a line they cannot
 * touch but which is about to be written - and the point of moving it rather than dropping it is
 * that it is theirs to adjust or delete. It is flagged, not fenced off.
 */
const isNonEditableRow = (row: Row) => isRemovedCopyItem(row) || isRemovedReviewState(getReviewState(row));

const renderCopyStatusIcon = (
  rowId: string,
  status: CopyStatus,
  previousAmount?: number,
  removedReason?: string,
  movedToCategoryName?: string,
) => {
  const formatAmount = (amount?: number) => (amount != null ? `$${Math.round(amount).toLocaleString()}` : '');

  switch (status) {
    case COPY_STATUS.CARRIED_OVER:
      return TooltipIcon({
        id: `item-copy-${rowId}`,
        icon: faSolidCircleCheck,
        iconColor: 'green',
        tooltip: 'Carried over from source budget.',
        placement: 'right',
      });
    case COPY_STATUS.PRICE_CHANGED:
      return TooltipIcon({
        id: `item-copy-${rowId}`,
        icon: faTriangleExclamation,
        iconColor: 'orange',
        tooltip: `Price changed from source budget (was ${formatAmount(previousAmount)}).`,
        placement: 'right',
      });
    case COPY_STATUS.REMOVED:
      return TooltipIcon({
        id: `item-copy-${rowId}`,
        icon: faCircleXmark,
        iconColor: 'red',
        // Two different removals, two different explanations. A service dated outside its category's
        // period IS still on the target price plan, so the plan-membership wording told a provider
        // something untrue about a service they could see sitting on the plan - which is the reason
        // the old message read as a bug rather than an explanation.
        tooltip:
          removedReason === REMOVED_REASON_UNAVAILABLE_FOR_DATES
            ? 'This service is not available under this contribution category for the dates being copied into.'
            : 'Service no longer available in the target price plan.',
        placement: 'right',
      });
    case COPY_STATUS.CATEGORY_MOVED:
      return TooltipIcon({
        id: `item-copy-${rowId}`,
        icon: faArrowRightArrowLeft,
        iconColor: 'dodgerblue',
        tooltip: movedToCategoryName
          ? `Moved to ${movedToCategoryName} - this service's contribution category changes over the new dates.`
          : "Moved to the contribution category that applies - this service's category changes over the new dates.",
        placement: 'right',
      });
    case COPY_STATUS.NEW:
      return TooltipIcon({
        id: `item-copy-${rowId}`,
        icon: faCircleExclamation,
        iconColor: 'dodgerblue',
        tooltip: 'New service available in the target price plan.',
        placement: 'right',
      });
    default:
      return false;
  }
};

const renderReviewStateIcon = (rowId: string, reviewState: ReviewState) => {
  switch (reviewState) {
    case REVIEW_STATE.NOT_ON_PLAN:
      return TooltipIcon({
        id: `item-review-${rowId}`,
        icon: faCircleXmark,
        iconColor: 'red',
        tooltip: 'This service does not exist on the price plan - This will be removed on save.',
        placement: 'right',
      });
    case REVIEW_STATE.FILTERED:
      return TooltipIcon({
        id: `item-review-${rowId}`,
        icon: faTriangleExclamation,
        iconColor: 'orange',
        tooltip: 'This service has been filtered.',
        placement: 'right',
      });
    case REVIEW_STATE.PRICE_MISSING:
      return TooltipIcon({
        id: `item-review-${rowId}`,
        icon: faCircleExclamation,
        iconColor: 'orange',
        tooltip: 'Missing Price - Please review before saving',
        placement: 'right',
      });
    default:
      return false;
  }
};

/**
 * A money cell that waits rather than showing a figure it knows is stale.
 *
 * A row whose quantity has just changed still holds the price and total computed for the previous
 * quantity, and the two are indistinguishable on screen - the provider has no way to tell whether
 * the number in front of them reflects the edit they just made or the state before it. Showing the
 * spinner until the recalculation lands is the difference between "not yet" and a wrong answer.
 */
const renderMoneyCell = (columnId: string) => {
  // Named rather than returned as a bare arrow: the lint rule that catches anonymous components
  // reads any capitalised-or-returned render function as one, and a nameless renderer is unhelpful
  // in a stack trace regardless.
  const renderCell = (value: any, row: Row, context?: EditorContext) => {
    const pending = (context?.pendingItemIds as Set<string> | undefined)?.has(String(row?.id));

    if (!pending) {
      // Not our formatting to own - a budget figure has to read exactly like every other currency
      // cell in the application, so the shared renderer is used verbatim and this only stands in
      // front of it.
      return currencyCellRender(columnId, true, undefined, {
        id: columnId,
        classNames: { cell: 'cell-align-right' },
      } as any)(Number(value), row);
    }

    return createElement(
      'div',
      { className: 'cell readonly cell-align-right' },
      createElement(
        'span',
        { className: 'totals-pending', role: 'status', 'aria-label': 'Recalculating' },
        createElement(Icon, { icon: faSpinner, spin: true, className: 'icon-small' }),
      ),
    );
  };

  return renderCell;
};

/**
 * A line's date, highlighted when the refresh control has just moved it.
 *
 * Same treatment the section header gives a refreshed co-contribution, for the same reason: a
 * refresh that silently rewrote dates would leave the provider unable to see what it had done.
 */
const renderLineDate = (value: any, row: Row, context?: EditorContext) => {
  const text = value ? formatDateString(value) : 'N/A';

  // The same `cell readonly` wrapper genericCellRender applies. Without it this cell loses the
  // padding every other read-only cell has and sits flush against the column edge.
  const content = isDateRefreshed(row, context)
    ? // Same bg-warning the section header uses for a refreshed co-contribution, so a moved date
      // reads as the same kind of event rather than a new one the provider has to learn.
      createElement(
        'span',
        {
          className: 'bg-warning rounded px-1',
          title: 'Updated to match the period this service is available for.',
        },
        text,
      )
    : text;

  return createElement('div', { className: 'cell readonly cell-align-left' }, content);
};

/**
 * A date editor bounded by the period its row's service is available for.
 *
 * The bounds are the point: a provider picking dates for a service that ends on 30 September should
 * not be able to reach 1 October in the first place, rather than reaching it and being rejected on
 * save. The save-time check stays as the backstop for rows that were dated before the service
 * period changed under them.
 */
const servicePeriodDateEditor =
  (columnId: string, filteredServices: any) =>
  (value: any, row: Row, onChange: (val: any) => void, context?: EditorContext, isDisabled = false) => {
    const detail = rowServiceDetail(row, context, filteredServices);

    // Narrowest of the two windows the line has to sit inside: its service's availability and the
    // budget's own period.
    const minDate = latestDate(detail?.period?.start_date, context?.start_date as string | undefined);
    const maxDate = earliestDate(detail?.period?.end_date, context?.end_date as string | undefined);

    return dateEditor(columnId, minDate, maxDate, { id: columnId, classNames: { cell: 'cell-align-left' } } as any)(
      value,
      row,
      onChange,
      context,
      isDisabled,
    );
  };

const latestDate = (left?: string | null, right?: string | null) => {
  if (!left) return right ?? undefined;
  if (!right) return left;
  return left > right ? left : right;
};

const earliestDate = (left?: string | null, right?: string | null) => {
  if (!left) return right ?? undefined;
  if (!right) return left;
  return left < right ? left : right;
};

/**
 * Per-cell validation for a line's dates.
 *
 * Written as a Yup test rather than a plain schema so it can read the rest of the row - the service
 * type and the section's category together are what identify which service period applies. A
 * failure marks the cell through the table's existing invalid-cell handling and names the range the
 * provider has to work within, rather than reporting only that something is wrong.
 */
const lineDateValidation = (filteredServices: any) =>
  Yup.string()
    .required('Date is required')
    .test('within-service-period', 'Date is outside the service period', function validateLineDate() {
      const row = this.parent as Row;
      const detail = rowServiceDetail(row, undefined, filteredServices);
      if (!detail) return true;
      if (isWithinPeriod({ start_date: row?.start_date ?? null, end_date: row?.end_date ?? null }, detail.period)) {
        return true;
      }

      return this.createError({
        message: `${detail.name ?? 'This service'} is only available ${formatRange(detail.period)}.`,
      });
    });

// Twelve editable columns compete for the width of one panel, so every column but Service Type is
// fixed and Service Type takes what is left. Sized against the minimal density's trimmed controls
// - each figure is what the column needs to show its widest value WITHOUT truncating, measured
// with the column rendered as an editor, which is always the wider case.
//
//   Date     - "01/07/2026" beside the trimmed calendar button.
//   Shift    - "Weekday Daytime" wraps to two lines rather than taking a 150px column for itself.
//   Weekdays - three weekday pills per line at the trimmed chip size.
//   Freq.    - "Fortnightly" plus the select caret on ONE line. Measured: 75px of text and a
//              34px indicator. Anything narrower wraps, and minimal's `word-break` then breaks it
//              mid-word as "Fortnightl / y".
//   Qty      - four characters ("12.5"); the header is what used to set this column's width, so
//              it is abbreviated and explained in its caption instead.
//   Ext Ref  - a checkbox, plus a reference field once ticked. Sized so the field is still
//              readable in the ticked state rather than only the checkbox fitting.
//   PH       - a checkbox and nothing else.
//   Price /
//   Total    - a five-figure amount with its dollar sign. Set by the totals panel below rather
//              than by the figure: the two have to share a width to line up, and "Contribution"
//              in the totals heading is the wider of the two things that must fit.
const WARNING_PX = 34;
const DATE_PX = 112;
const SHIFT_PX = 116;
const WEEKDAYS_PX = 150;
const FREQUENCY_PX = 126;
const QUANTITY_PX = 54;
const EXTERNAL_PROVIDER_PX = 124;
const PUBLIC_HOLIDAY_PX = 44;
const CURRENCY_PX = 104;
const ACTIONS_PX = 76;
// The narrowest Service Type may be squeezed to before the table scrolls instead. Below roughly
// this, a name like "SAH - Assistance with Self-Administration of Medication" stops wrapping
// between words and starts breaking mid-word over six or seven lines.
const SERVICE_TYPE_MIN_PX = 190;

const px = (value: number) => `${value}px`;

/**
 * The width below which the items table should scroll rather than keep compressing.
 *
 * Under `table-layout: fixed` a fluid column has nothing stopping its fixed neighbours squeezing
 * it to nothing, so the floor goes on the table and the wrapper scrolls - the honest outcome when
 * the columns genuinely do not fit.
 */
export const minTableWidth =
  WARNING_PX +
  SERVICE_TYPE_MIN_PX +
  DATE_PX * 2 +
  SHIFT_PX +
  WEEKDAYS_PX +
  FREQUENCY_PX +
  QUANTITY_PX +
  EXTERNAL_PROVIDER_PX +
  PUBLIC_HOLIDAY_PX +
  CURRENCY_PX * 2 +
  ACTIONS_PX;

export const columnsFactory = (
  shifts: Shift[],
  frequencies: Lookup[],
  filteredServices: any,
  _initialValues?: Section,
): Column[] => {
  const weekdayOptions = getDaysMultiSelectOptions().map(({ value, label }) => ({ id: value, name: label }));
  const frequencyOptions = frequencies.map(({ id, name }) => ({ id, name }));
  const shiftOptions = shifts.map(({ id, name }) => ({ id, name }));
  const serviceTypeOptions = filteredServices.map(({ id, name, sah_service: { category } }: ServiceType) => ({
    id,
    name,
    category_id: category?.id,
  }));

  // To be reimplemented for styling change to price / total on edit if needed
  // const initialItemValues = (rowId: string): SectionItem | undefined => {
  //   return initialValues?.items.find(({ id }) => id === rowId);
  // };

  const columns: Column[] = [
    {
      id: 'warning',
      header: '',
      type: 'readonly',
      style: { width: px(WARNING_PX) },
      classNames: {
        cell: 'cell-align-center text-warning',
      },
      renderReadOnly: (value, row, context) => {
        // A date problem outranks everything else, INCLUDING a copy status, because it is the one
        // state here that stops the budget being saved at all. A copy annotates every line it
        // touches, so checking copy status first rendered a line dated outside its service period
        // with the same green tick as one that came across cleanly - the save then failed naming a
        // line that both this step and the Review step had called valid. Checked ahead of the copy
        // status rather than inside it so the two steps, which share this column, agree.
        //
        // Named specifically, too. Falling through to the generic "no longer valid or has been
        // filtered" below would tell a provider whose only problem is a date that their service is
        // gone, which is both wrong and unactionable - the range they need is known.
        //
        // A line the copy already dropped is checked before either date rule. findDateInvalidItems
        // skips removed lines deliberately - flagging one would ask a provider to fix something the
        // application has already dealt with - and on this step the row is disabled with no delete
        // control, so a date-outside-period reading here would tell them to re-date a line they can't
        // touch, and would drop the one thing they can act on: that it was removed.
        if (isRemovedCopyItem(row)) {
          return renderCopyStatusIcon(
            String(row.id),
            COPY_STATUS.REMOVED,
            row.previous_unit_amount,
            row.removed_reason as string | undefined,
          );
        }

        if (isOutsideBudget(row, context)) {
          return TooltipIcon({
            id: `item-date-budget-${row.id}`,
            icon: faTriangleExclamation,
            iconColor: 'red',
            tooltip: `This line is dated outside the budget period ${formatRange({
              start_date: (context?.start_date as string) ?? null,
              end_date: (context?.end_date as string) ?? null,
            })}.`,
            placement: 'right',
          });
        }

        if (isOutsideServicePeriod(row, context, filteredServices)) {
          const detail = rowServiceDetail(row, context, filteredServices);
          return TooltipIcon({
            id: `item-date-service-${row.id}`,
            icon: faTriangleExclamation,
            iconColor: 'red',
            tooltip: `${detail?.name ?? 'This service'} is only available ${formatRange(detail?.period ?? null)}.`,
            placement: 'right',
          });
        }

        // Copy status indicators take priority over the plan-membership fallbacks below.
        const copyStatus = row?.copy_status as CopyStatus | undefined;
        if (copyStatus) {
          return renderCopyStatusIcon(
            String(row.id),
            copyStatus,
            row.previous_unit_amount,
            row.removed_reason as string | undefined,
            (row.moved_to_category as SahCategory | undefined)?.name,
          );
        }

        // Flagged review states (returned by get_one) take priority over the fallback checks below.
        // A non-flagged state (on_plan) must fall through so the live "ignore SAH filters" toggle is
        // still reflected client-side (the server-computed review_state reflects the *saved* toggle only).
        const reviewState = getReviewState(row);
        if (reviewState && isFlaggedReviewState(reviewState)) {
          return renderReviewStateIcon(String(row.id), reviewState);
        }

        if (!value) return false;

        // Check if service is broken (invalid/missing)
        const isBroken = !isMatchedService(row, context, filteredServices);

        if (isBroken) {
          return TooltipIcon({
            id: `item-warning-${row.id}`,
            icon: faTriangleExclamation,
            iconColor: 'red',
            tooltip: 'Service is no longer valid or has been filtered.',
            placement: 'right',
          });
        }

        return TooltipIcon({
          id: `item-info-${row.id}`,
          icon: faInfoCircle,
          iconColor: 'orange',
          tooltip: 'Service available because SAH filters are ignored.',
          placement: 'right',
        });
      },
      deriveValue: (row: Row, context?: EditorContext) => {
        if (row?.copy_status) return true;
        const reviewState = getReviewState(row);
        if (reviewState && isFlaggedReviewState(reviewState)) return true;
        if (isOutsideBudget(row, context)) return true;
        if (isOutsideServicePeriod(row, context, filteredServices)) return true;
        const isBroken = !isMatchedService(row, context, filteredServices);
        const isIgnored = isIgnoredService(row, context, filteredServices);
        return isBroken || isIgnored;
      },
    },
    {
      id: 'service_type_id',
      header: 'Service Type',
      type: 'select',
      options: serviceTypeOptions,
      // Deliberately unstyled: the one fluid column, taking whatever the fixed ones leave. Its
      // floor is enforced on the table through `minTableWidth`.
      classNames: {
        cell: 'cell-align-left',
      },
      onChange: onChangeServiceType(filteredServices),
      // A select cell reads its label off the options, and a line the copy is dropping often has no
      // option to read: the service list is fetched for the dates being copied INTO, so a service
      // only available before them is not in it. That left the one column identifying the row blank
      // on exactly the rows a provider needs to identify - they could see something had been removed
      // but not what. The line carries its own service from the server, so fall back to that.
      //
      // Wrapped through genericCellRender rather than returning the name directly, because the cell
      // div it builds is what carries `classNames.cell` - and without it this column lost its left
      // alignment on every read-only rendering of the table, which is the whole of the Review step.
      renderReadOnly: genericCellRender(
        (value, row) =>
          serviceTypeOptions.find((option: { id: string }) => option.id === value)?.name ??
          row?.service_type?.name ??
          '',
        undefined,
        { classNames: { cell: 'cell-align-left' } } as Column,
      ),
      renderEditor: selectLazyEditor(
        (_row, context) => {
          return serviceTypeOptions.filter((option: any) => option.category_id === context.category_id);
        },
        (row) => (row.service_type ? { id: row.service_type.id, name: row.service_type.name } : null),
        false,
      ),
      isEditable: (row: Row, context?: EditorContext) =>
        !isNonEditableRow(row) && isMatchedService(row, context, filteredServices),
      validation: Yup.number().required('Service Type is required'),
    },
    {
      id: 'start_date',
      header: 'Start',
      caption: 'The date this line starts, within both the budget period and the service period',
      type: 'date',
      style: { width: px(DATE_PX) },
      classNames: {
        cell: 'cell-align-left',
      },
      renderReadOnly: renderLineDate,
      renderEditor: servicePeriodDateEditor('start_date', filteredServices),
      isEditable: (row: Row, context?: EditorContext) =>
        !isNonEditableRow(row) && isMatchedService(row, context, filteredServices),
      validation: lineDateValidation(filteredServices),
    },
    {
      id: 'end_date',
      header: 'End',
      caption: 'The date this line ends, within both the budget period and the service period',
      type: 'date',
      style: { width: px(DATE_PX) },
      classNames: {
        cell: 'cell-align-left',
      },
      renderReadOnly: renderLineDate,
      renderEditor: servicePeriodDateEditor('end_date', filteredServices),
      isEditable: (row: Row, context?: EditorContext) =>
        !isNonEditableRow(row) && isMatchedService(row, context, filteredServices),
      validation: lineDateValidation(filteredServices),
    },
    {
      id: 'shift_id',
      header: 'Shift',
      type: 'select',
      style: { width: px(SHIFT_PX) },
      classNames: {
        cell: 'cell-align-left',
      },
      options: shiftOptions,
      onChange: async (_row, _context) => {
        return {
          week_days: [],
        };
      },
      isEditable: (row: Row, context?: EditorContext) =>
        !isNonEditableRow(row) && isMatchedService(row, context, filteredServices) && !isInvoiceType(row),
    },
    {
      id: 'week_days',
      header: 'Days',
      caption: 'Weekdays this service is delivered on',
      type: 'multi-lookup',
      style: { width: px(WEEKDAYS_PX) },
      classNames: {
        cell: 'cell-align-left',
      },
      options: weekdayOptions,
      renderEditor: multiSelectEditor((row) => {
        if (!row.shift_id) return [];

        const shift = shifts.find(({ id }) => id === row.shift_id);
        const allow_weekdays = shift?.allow_weekdays ?? [];
        if (allow_weekdays.length === 0) return [];

        return weekdayOptions.filter(({ id }) => allow_weekdays.includes(Number(id)));
      }, true),
      isEditable: (row: Row, context?: EditorContext) =>
        !isNonEditableRow(row) &&
        row.shift_id &&
        isMatchedService(row, context, filteredServices) &&
        !isInvoiceType(row),
    },
    {
      id: 'frequency_id',
      header: 'Freq.',
      caption: 'How often the service repeats',
      type: 'select',
      style: { width: px(FREQUENCY_PX) },
      classNames: {
        cell: 'cell-align-left',
      },
      options: frequencyOptions,
      isEditable: (row: Row, context?: EditorContext) =>
        !isNonEditableRow(row) && isMatchedService(row, context, filteredServices) && !isInvoiceType(row),
    },
    {
      id: 'quantity',
      header: 'Qty',
      caption: 'Hours for an hourly service, or quantity for any other',
      type: 'number',
      classNames: {
        header: 'cell-align-center',
        cell: 'cell-align-center',
      },
      style: { width: px(QUANTITY_PX) },
      isEditable: (row: Row, context?: EditorContext) =>
        !isNonEditableRow(row) && isMatchedService(row, context, filteredServices) && !isInvoiceType(row),
    },
    {
      id: 'external_provider',
      header: 'Ext. Ref',
      caption: 'External provider reference, generated automatically if left blank',
      type: 'custom',
      component: ExternalProvider,
      classNames: {
        header: 'cell-align-center',
        cell: 'cell-align-center checkbox-cell',
      },
      style: { width: px(EXTERNAL_PROVIDER_PX) },
      deriveValue: ({ is_external_provider, external_reference, billing_type }) => {
        return {
          is_external_provider: is_external_provider ?? false,
          external_reference: external_reference ?? '',
          billing_type,
        };
      },
      onChange: onChangeExternalProvider,
      isEditable: (row: Row, context?: EditorContext) =>
        !isNonEditableRow(row) && isMatchedService(row, context, filteredServices),
    },
    {
      id: 'include_public_holidays',
      header: 'PH',
      caption: 'Include public holidays in this line',
      type: 'checkbox',
      classNames: {
        header: 'cell-align-center',
        cell: 'cell-align-center checkbox-cell',
      },
      style: { width: px(PUBLIC_HOLIDAY_PX) },
      isEditable: (row: Row, context?: EditorContext) =>
        !isNonEditableRow(row) && isMatchedService(row, context, filteredServices) && !isInvoiceType(row),
    },
    {
      id: 'unit_amount',
      header: 'Price',
      type: 'currency',
      classNames: {
        header: 'cell-align-right',
        cell: 'cell-align-right',
      },
      componentProps: {
        noDecimals: true,
      },
      // Only the read-only case is rendered here. An invoice line's price is entered by the
      // provider, so it takes the editor path and is never waiting on the server for its value.
      renderReadOnly: renderMoneyCell('unit_amount'),
      style: { width: px(CURRENCY_PX) },
      isEditable: (row: Row, context?: EditorContext) =>
        !isNonEditableRow(row) && isMatchedService(row, context, filteredServices) && isInvoiceType(row),
    },
    {
      id: 'total_amount',
      header: 'Total',
      type: 'currency',
      classNames: {
        header: 'cell-align-right px-1',
        column: 'px-1',
        cell: 'cell-align-right',
      },
      componentProps: {
        noDecimals: true,
      },
      renderReadOnly: renderMoneyCell('total_amount'),
      style: { width: px(CURRENCY_PX) },
      isEditable: false,
    },
  ];

  return columns;
};

/**
 * Picking a service type resets the line, and now dates it too.
 *
 * The dates are the budget's own period narrowed to the part of it the chosen service is available
 * for. A service that ends part way through the budget gives a line ending with it, and its
 * replacement - a separate entry, under its own contribution category, in its own section - gives a
 * line covering the rest. That is what makes a budget spanning a category change come out right
 * without anyone entering dates by hand.
 *
 * Where the service covers the whole budget the dates are simply the budget's, which is what the
 * line would have carried before.
 */
const onChangeServiceType = (filteredServices: any) => async (row: Row, context?: EditorContext) => {
  const columnsToReset = [
    'shift_id',
    'week_days',
    'frequency_id',
    'quantity',
    'external_provider',
    'include_public_holidays',
    'unit_amount',
    'total_amount',
    'billing_type',
  ];

  const resetValues = columnsToReset.reduce(
    (acc, key) => {
      if (key === 'week_days') acc[key] = [];
      else if (key === 'include_public_holidays') acc[key] = false;
      else if (key === 'external_provider') {
        acc.is_external_provider = false;
        acc.external_reference = '';
      } else acc[key] = null;
      return acc;
    },
    {} as Record<string, any>,
  );

  const budgetRange: DateRange = {
    start_date: (context?.start_date as string) ?? null,
    end_date: (context?.end_date as string) ?? null,
  };
  const detail = rowServiceDetail(row, context, filteredServices);
  const dates = deriveItemDates(budgetRange, detail?.period ?? null);

  // A service available for no part of the budget leaves the dates alone. There is nothing correct
  // to write, and the row will be flagged and rejected on save rather than quietly emptied.
  if (dates?.start_date && dates?.end_date) {
    resetValues.start_date = dates.start_date;
    resetValues.end_date = dates.end_date;
  }

  return resetValues;
};

const onChangeExternalProvider = async (row: Row, _context?: EditorContext, key?: string) => {
  if (!key) return {};
  const val = row[key as keyof Row];
  if (val == null) {
    return {
      is_external_provider: false,
      external_reference: '',
    };
  }

  const v = val as {
    is_external_provider?: boolean;
    external_reference?: string;
  };

  const isExternal = !!v.is_external_provider;

  return {
    is_external_provider: isExternal,
    external_reference: isExternal ? (v.external_reference ?? '') : '',
  };
};
